import {
    Button,
    Form,
    Loading,
    Spacer,
    StyleFunction,
    useThemedStyle,
} from "@venuepos/react-common";
import { useTranslation } from "locales";
import React, { useCallback } from "react";
import { View } from "react-native";

import type { GridDimensions, ILayoutInput, ProductSection } from "lib";
import { ProductLayoutProvider } from "../product-layout-provider";
import { LayoutGridSection } from "./layout-grid-section";
import { SectionTabs } from "./section-tabs";
import { FloatingButtonContainer } from "../../../components/floating-button-container";
import { LayoutReferenceData } from "./product-layout-operation-reducer";

export function LayoutEditor({
    form,
    onSubmit,
    referenceData,
    dimensions,
}: {
    form: Form<ILayoutInput>;
    onSubmit: () => Promise<void>;
    referenceData: LayoutReferenceData;
    dimensions: GridDimensions;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values }, { setValue }] = form;

    const handleLayoutUpdate = useCallback(
        (value: ILayoutInput["data"]) => {
            setValue("data", value);
        },
        [setValue]
    );

    if (!values?.data) {
        return <Loading />;
    }

    return (
        <View testID="layout:editScreen">
            <ProductLayoutProvider
                gridDimensions={dimensions}
                referenceData={referenceData}
                sections={values.data as ProductSection[]}
                onLayoutUpdate={handleLayoutUpdate}
            >
                <SectionTabs />
                <LayoutGridSection />
                <Spacer />
                <FloatingButtonContainer>
                    <View style={styles.buttonHolder}>
                        <Button
                            onPress={onSubmit}
                            testID="layout:saveLayout"
                            shadow="shadow"
                            style={styles.saveButton}
                        >
                            {t("common.save", "Save")}
                        </Button>
                    </View>
                </FloatingButtonContainer>
            </ProductLayoutProvider>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    buttonHolder: {
        ...theme.styles.row,
        gap: theme.spacingScale,
    },

    saveButton: {
        minWidth: 150,
    },
});
